import * as React from 'react';
import {
  getComponentState,
  getDynamicData,
  logError,
  setComponentState,
  translate,
  URLX,
  useMatchSmallViewport,
} from '@avensia/excite';
import { styled } from '@glitz/react';
import RatingStars from './RatingStars';
import { Theme } from 'webapp/theme';
import { StarIcon } from 'svenssons/shared/icons';
import { createSlideHeightAnimationComponent } from '@avensia/excite-ui';
import { createMaxSmallViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import { desktopEdgeMarginDecorator, maxContentNormalWidthDecorator } from 'webapp/components/page-layout';
import { Button } from 'webapp/components/button';
import { FieldSize } from 'webapp/components/field-size';
import { bareListDecorator } from 'webapp/decorators/list';
import { Review, type TestFreaksReview } from './Review';
import { black, shadedGrey } from 'webapp/shared/sharedTheme';

interface TestFreaksSummary {
  your_review_count: number;
  your_avg_score: number;
  pro_review_count: number;
  user_review_count: number;
  score: number;
  your_review_url: string;
  your_score_dist: number[];
  your_review_answers: string;
  feedback_enabled: boolean;
  write_review_url: string;
}

interface TestFreaksReviewsReponse {
  next_page_url: string;
  reviews: TestFreaksReview[];
}

export default React.memo(
  React.forwardRef<HTMLDivElement, Excite.TestFreaks.ITestFreaksApiModel>(function Reviews(props, ref) {
    const [summary, setSummary] = React.useState<TestFreaksSummary | undefined>(
      getComponentState<TestFreaksSummary | undefined>('summary', undefined),
    );
    const [reviewsResponse, setReviewsResponse] = React.useState<TestFreaksReviewsReponse | undefined>(
      getComponentState<TestFreaksReviewsReponse | undefined>('reviews', undefined),
    );

    const isSmallViewport = useMatchSmallViewport();

    React.useEffect(() => {
      if (!summary || !reviewsResponse) {
        fetchReviews(props.testFreaksFamilyId, props.testFreaksProductId, setReviewsResponse, setSummary);
      }
    }, [props, reviewsResponse, summary]);

    const heading = <Heading>{translate(t => t.productPage.detailsReviewHeading)}</Heading>;

    return (
      <div ref={ref} data-scroll-id>
        {summary ? (
          <Base>
            {!isSmallViewport && heading}
            <Content>
              <Summary>
                {isSmallViewport && heading}
                {summary && (
                  <AverageScoreContainer>
                    <AverageRating>{summary.your_avg_score}</AverageRating>
                    <ReviewCountContainer>
                      <RatingStars rating={summary.your_avg_score} />
                      {!isSmallViewport && (
                        <ReviewCount>
                          {translate(t => t.productPage.reviewsCount, { count: summary.your_review_count })}
                        </ReviewCount>
                      )}
                    </ReviewCountContainer>
                  </AverageScoreContainer>
                )}

                {!isSmallViewport &&
                  summary?.your_score_dist.map((count, i) => (
                    <SummaryBarContainer key={i}>
                      <Star />
                      <NumberOfStars>{5 - i}</NumberOfStars>
                      <SummaryBar>
                        <Progress style={{ width: (count / summary.your_review_count) * 100 + '%' }} />
                      </SummaryBar>
                      <Count>{count}</Count>
                    </SummaryBarContainer>
                  ))}
              </Summary>
              <ReviewList>
                {reviewsResponse?.reviews.map((review, i) => (
                  <Review key={review.author + review.date} review={review} />
                ))}
              </ReviewList>
            </Content>
            {reviewsResponse?.next_page_url && (
              <ShowMoreButton
                size={FieldSize.Small}
                onClick={reviewsResponse ? () => loadMoreReviews(reviewsResponse, setReviewsResponse) : undefined}
                as={Theme.Transparent}
                disableRippleEffect
              >
                {translate(t => t.productPage.showMoreReviews)}
              </ShowMoreButton>
            )}
          </Base>
        ) : null}
      </div>
    );
  }),
);

const Star = styled(StarIcon, {
  height: 14,
  width: 13,
});

const Base = styled(
  createSlideHeightAnimationComponent(styled.Section),
  maxContentNormalWidthDecorator,
  desktopEdgeMarginDecorator,
  createMinLargeViewportDecorator({
    marginBottom: 90,
  }),
  {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: t => t.sharedSpacing.xl,
  },
);

const Heading = styled.h2(
  createMinLargeViewportDecorator({
    marginBottom: t => t.sharedSpacing.m,
  }),
  {
    fontWeight: t => t.fontWeight?.bold,
    fontSize: t => t.fontSize.l,
    marginBottom: 0,
  },
);

const Content = styled.div(
  createMinLargeViewportDecorator({
    display: 'grid',
    gridTemplateColumns: '400px auto',
  }),
  {
    fontSize: t => t.sharedFontSize.xs,
    width: '100%',
  },
);

const AverageRating = styled.span(
  createMinLargeViewportDecorator({ fontSize: 64 }),
  createMaxSmallViewportDecorator({
    fontSize: t => t.sharedFontSize.m,
    fontWeight: 'bold',
  }),
);

const Summary = styled.div(
  createMaxSmallViewportDecorator({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: { x: t => t.sharedSpacing.m, y: 0 },
    height: 72,
    borderBottom: { width: 'thin', color: t => t.borderColor, style: 'solid' },
  }),
  {
    padding: { right: t => t.sharedSpacing.xxl },
  },
);

const ReviewList = styled.ul(bareListDecorator, {
  display: 'flex',
  flexDirection: 'column',
  gap: t => t.sharedSpacing.l,
  marginTop: t => t.sharedSpacing.l,
});

const AverageScoreContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
});

const ReviewCount = styled.p({
  marginBottom: 0,
});

const ReviewCountContainer = styled.div({
  marginLeft: t => t.sharedSpacing.l,
});

const SummaryBarContainer = styled.div({
  display: 'grid',
  gridTemplateColumns: 'auto 33px minmax(190px, 1fr) 50px;',
  alignItems: 'center',
  marginTop: 4,
});

const Progress = styled.div({
  height: 'inherit',
  borderRadius: 'inherit',
  backgroundColor: black,
});

const Count = styled.span({
  paddingLeft: t => t.sharedSpacing.m,
});

const NumberOfStars = styled.span({
  padding: { right: t => t.sharedSpacing.m, left: t => t.sharedSpacing.xs },
});

const SummaryBar = styled.div({
  backgroundColor: shadedGrey,
  borderRadius: 50,
  height: 4,
});

const ShowMoreButton = styled(
  Button,
  createMinLargeViewportDecorator({
    ':hover': {
      textDecoration: 'underline',
    },
  }),
  {
    alignSelf: 'center',
  },
);

async function loadMoreReviews(
  reviewsResponse: TestFreaksReviewsReponse,
  setReviews: React.Dispatch<TestFreaksReviewsReponse>,
) {
  if (reviewsResponse) {
    const url = new URLX(reviewsResponse.next_page_url);
    url.searchParams.set('limit', '10');
    const response = await fetch(url.toString());
    const json: TestFreaksReviewsReponse = await response.json();
    const state = { next_page_url: json.next_page_url, reviews: [...reviewsResponse.reviews, ...json.reviews] };
    setComponentState({ id: 'reviews', state });
    setReviews(state);
  }
}

async function fetchReviews(
  testFreaksFamilyId: string,
  testFreaksProductId: string,
  setReviews: React.Dispatch<TestFreaksReviewsReponse>,
  setSummary: React.Dispatch<TestFreaksSummary>,
) {
  try {
    const { clientId } = await getDynamicData<Excite.TestFreaks.TestFreaksClientId>().current();
    const summaryResponse = await fetch(
      `https://js.testfreaks.com/onpage/${clientId}/reviews.json?key=${testFreaksProductId}&url_key=${testFreaksFamilyId}&limit=3`,
    );
    const summary: TestFreaksSummary = await summaryResponse.json();
    if (summary) {
      summary.your_score_dist.reverse();
      setComponentState({ id: 'summary', state: summary });

      setSummary(summary);

      const response = await fetch(summary.your_review_url);
      const reviews: TestFreaksReviewsReponse = await response.json();
      setComponentState({ id: 'reviews', state: reviews });

      setReviews(reviews);
    }
  } catch (e) {
    logError('couldnt fetch reviews');
  }
}
